/**
 * M2M (Mark-to-Market) Service — API client for M2M snapshot endpoints
 */

import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../sp-firebase/firebase';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

// ─── Types ───────────────────────────────────────────────────────────

export interface M2mGroup {
  commodity: string;
  contractMonth: string;
  lots: number;
  avgTradePrice: number;
  closePrice: number;
  currency: string;
  positionSide: 'long' | 'short';
  openPnl: number;
  formulaCode: string | null;
  lotSize: number | null;
  // Option-specific (only set for CALL/PUT)
  isOption?: boolean;
  optionType?: 'call' | 'put';
  strikePrice?: number;
  premiumPaid?: number;
  premiumCurrent?: number;
}

export interface M2mFinancialSummary {
  beginningBalanceTotal: number;
  endingBalanceTotal: number;
  marginExcess: number;
  initialMargin: number;
  maintenanceMargin: number;
  futOpenTradeEquity: number;
  acctValueAtMarket: number;
}

export interface M2mFinancialSegment {
  segmentLabel: string;
  currency: string;
  beginningBalance: number;
  cashActivity: number;
  endingBalance: number;
  futOpenTradeEquity: number;
  totalEquity: number;
  acctValueAtMarket: number;
  initialMarginRequirement: number;
  maintenanceMarginRequirement: number;
  marginDeficit: number;
  marginExcess: number;
  conversionRateToUsd: number;
  convertedAccountValueUsd: number;
}

export interface M2mSnapshot {
  id: number;
  accountId: number;
  statementDate: string;
  brokerAccount: string;
  groups: M2mGroup[];
  financialSummary: M2mFinancialSummary;
  financialSegments: M2mFinancialSegment[];
  uploadBatchId: number | null;
  createdAt: string;
}

// ─── Auth helper ─────────────────────────────────────────────────────

function waitForFirebaseUser(timeoutMs: number = 5000): Promise<User | null> {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), timeoutMs);
    const unsub = onAuthStateChanged(auth, (user) => {
      clearTimeout(timer);
      unsub();
      resolve(user);
    });
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = await waitForFirebaseUser();
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

// ─── API calls ───────────────────────────────────────────────────────

export async function getLatestM2mSnapshot(accountId: string): Promise<M2mSnapshot | null> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/v2/m2m/latest?accountId=${accountId}`, { headers });
  if (!res.ok) throw new Error(`Failed to fetch M2M snapshot: ${res.status}`);
  const data = await res.json();
  return data.snapshot || null;
}

export async function getM2mHistory(accountId: string, limit: number = 90): Promise<M2mSnapshot[]> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/api/v2/m2m/history?accountId=${accountId}&limit=${limit}`, { headers });
  if (!res.ok) throw new Error(`Failed to fetch M2M history: ${res.status}`);
  const data = await res.json();
  return data.snapshots || [];
}
